import { RouteDescriptor, ExtractRoutePath } from './types';
import { createUseNavigation } from './helpers';
import { createLink } from './createLink';
import { createButton } from './createButton';
import { createRedirect } from './createRedirect';

import EmailSettings from '../Pages/EmailSettings';
import BuilderPage from '../Evaluation/BuilderPage';
import CurrencySettings from '../Pages/CurrencySettingsPage';
import CustomStyle from '../Settings/CustomStyle';
import EditPage from '../Evaluation/EditPage';
import EmbedInfo from '../EmbedInfo';
import FormBuilder from '../FormBuilder';
import IntegrationSettings from '../Settings/IntegrationSettings';
import InvoiceSettings from '../Settings/InvoiceSettings';
import MapEditor from '../MapEditor';
import MarketEditor from '../MarketEditor';
import VendorPortalPages from '../VendorPortalPages';
import VendorPortalSettings from '../Settings/VendorPortalSettings';
import Redemptions from '../MarketSales';
import FormOptions from '../FormBuilder/FormOptions';
import PaymentSettings from '../Pages/PaymentSettingsPage';
import PlacementEditor from '../PlacementEditor';
import PlacementEditorLegacy from '../PlacementEditorLegacy';
import PricingEditor from '../PricingEditor';
import SeasonsPage from '../Settings/SeasonsPage';
import SpotDesignation from '../Settings/SpotDesignation';
import SubmissionPage from '../Evaluation/SubmissionPage';
import VendorAttendance from '../VendorAttendance';
import VendorAccountsPage from '../Pages/VendorAccountsPage';
import AccountManagementPage from '../AccountManagement/AccountManagementPage';
import AccountDetailsPage from '../AccountManagement/AccountDetailsPage';
import AccountNewPage from '../AccountManagement/AccountNewPage';

import VendorChangesList from '../VendorChangesList';
import VendorChangeDetails from '../VendorChangeDetails';
import SendInvoicesPage from '../Pages/SendInvoicesPage';
import { BillingVendorListPage } from '../Pages/BillingVendorListPage';
import VendorPaymentDetailsPage from '../Pages/VendorPaymentDetailsPage';
import UsersPage from '../UsersPage';
import AddonsPage from '../Pages/AddonsPage';
import EmailListPage from '../Mailer/EmailListPage';
import EmailComposerPage from '../Mailer/EmailComposerPage';
import MailerSettingsPage from '../Mailer/SettingsPage';
import SelectRecipientsPage from '../Mailer/SelectRecipientsPage';
import SendPreviewPage from '../Mailer/SendPreviewPage';
import SendProgressPage from '../Mailer/SendProgressPage';
import SentSummaryPage from '../Mailer/SentSummaryPage';
import { VendorListPage } from '../Vendors/VendorListPage';
import { VendorDetailsPage } from '../Vendors/VendorDetailsPage';
import { FeeSettingsPage } from '../Billing/FeeSettings/FeeSettingsPage';
import { SalesPage } from '../Billing/Sales/SalesPage';
import { Account } from '../../types/shared/Account';

export function getRoutes(account: Account) {
  return defineRoutes([
    {
      type: 'page',
      path: '/vendors',
      name: t('Vendors'),
      icon: 'people',
      component: VendorListPage,
      requiresPermissions: { vendors: 'READ' },
    },
    {
      type: 'page',
      path: '/vendors/:id',
      name: t('Vendor Details'),
      icon: 'people',
      component: VendorDetailsPage,
      omitFromNav: true,
      requiresPermissions: { vendors: 'READ' },
    },
    {
      type: 'page',
      path: '/attendance',
      name: t('Attendance'),
      icon: 'listCheck',
      component: VendorAttendance,
      options: { legacyLayout: true },
      requiresPermissions: { attendance: 'READ' },
    },
    {
      type: 'parent',
      name: t('Billing'),
      icon: 'billing',
      requiresPermissions: { billing: 'READ' },
      routes: [
        {
          type: 'page',
          path: '/billing/vendors',
          name: t('Billing Summary'),
          component: BillingVendorListPage,
          requiresPermissions: { billing: 'READ' },
        },
        {
          type: 'page',
          path: '/billing/vendors/:id',
          name: t('Vendor Billing Details'),
          component: VendorPaymentDetailsPage,
          options: { legacyLayout: true },
          omitFromNav: true,
          requiresPermissions: { billing: 'READ' },
        },
        {
          type: 'page',
          path: '/billing/new-invoice',
          name: t('Send Invoice'),
          component: SendInvoicesPage,
          requiresPermissions: { billing: 'WRITE' },
        },
        {
          type: 'page',
          path: '/billing/settings',
          name: t('Payment Settings'),
          component: PaymentSettings,
          options: { legacyLayout: true },
          requiresPermissions: { billing: 'READ' },
        },
        {
          type: 'page',
          // This is an alias for the main link (below)
          alias: true,
          path: '/sales',
          name: t('Sales & Redemptions'),
          component: SalesPage,
          requiresPermissions: { marketSales: 'WRITE' },
        },
        {
          type: 'page',
          path: '/billing/fee-settings',
          name: t('Settings'),
          component: FeeSettingsPage,
          requiresPermissions: { billing: 'READ' },
        },
        {
          type: 'page',
          path: '/billing/settings/email',
          name: t('Invoice Email'),
          component: EmailSettings,
          props: {
            title: 'Invoice Email',
            titleVerb: 'invoice',
            settingsKey: 'invoice_email',
          },
          options: { legacyLayout: true },
          requiresPermissions: { billing: 'READ' },
        },
        {
          type: 'page',
          path: '/billing/settings/currency',
          name: t('Currency'),
          component: CurrencySettings,
          options: { legacyLayout: true },
          requiresPermissions: { billing: 'READ' },
        },
      ],
    },
    {
      type: 'parent',
      name: t('Market Reports'),
      icon: 'description',
      disabled: account.featureFlags?.hasLegacyEvalSubmissions !== true,
      routes: [
        {
          type: 'page',
          path: '/market-metrics/form',
          name: t('Form'),
          component: EditPage,
          options: { legacyLayout: true },
          requiresPermissions: { metrics: 'READ' },
        },
        {
          type: 'page',
          path: '/market-metrics/submissions',
          name: t('Submissions'),
          component: SubmissionPage,
          options: { legacyLayout: true },
          requiresPermissions: { metrics: 'READ' },
        },
        {
          type: 'page',
          path: '/market-metrics/builder',
          name: t('Report Builder'),
          component: BuilderPage,
          options: { legacyLayout: true },
          requiresPermissions: { metrics: 'WRITE' },
        },
      ],
      requiresPermissions: { metrics: 'READ' },
    },
    {
      type: 'parent',
      name: t('Market Sales'),
      icon: 'barChart',
      requiresPermissions: { marketSales: 'READ' },
      routes: [
        {
          type: 'page',
          path: '/sales',
          name: t('Sales & Redemptions'),
          component: SalesPage,
          requiresPermissions: { marketSales: 'WRITE' },
        },
        {
          type: 'page',
          path: '/redemptions',
          name: t('Redemptions (Old)'),
          component: Redemptions,
          options: { legacyLayout: true },
          requiresPermissions: { marketSales: 'READ' },
          disabled: account.featureFlags?.hasLegacySalesData !== true,
        },
      ],
    },
    {
      type: 'parent',
      name: t('Form Builder'),
      icon: 'description',
      requiresPermissions: { formBuilder: 'READ' },
      routes: [
        {
          type: 'page',
          path: '/form-builder/form-fields',
          name: t('Form Fields'),
          component: FormBuilder,
          props: { formID: 'signup' },
          options: { legacyLayout: true },
          requiresPermissions: { formBuilder: 'READ' },
        },
        {
          type: 'page',
          path: '/form-builder/form-options',
          name: t('Form Options'),
          component: FormOptions,
          props: { formID: 'signup' },
          options: { legacyLayout: true },
          requiresPermissions: { formBuilder: 'READ' },
        },
      ],
    },
    {
      type: 'page',
      path: '/markets',
      name: t('Markets & Dates'),
      icon: 'event',
      component: MarketEditor,
      options: { legacyLayout: true },
      requiresPermissions: { marketsAndPricing: 'READ' },
    },
    {
      type: 'parent',
      name: t('Pricing'),
      icon: 'money',
      requiresPermissions: { marketsAndPricing: 'READ' },
      routes: [
        {
          type: 'page',
          path: '/pricing/tiers',
          name: t('Price Tiers'),
          component: PricingEditor,
          options: { legacyLayout: true },
          requiresPermissions: { marketsAndPricing: 'READ' },
        },
        {
          type: 'page',
          path: '/pricing/addons',
          name: t('Addons'),
          component: AddonsPage,
          options: { legacyLayout: true },
          requiresPermissions: { marketsAndPricing: 'READ' },
        },
      ],
    },
    {
      type: 'parent',
      name: t('Email'),
      icon: 'description',
      // requiresPermissions: { marketsAndPricing: 'READ' },
      routes: [
        {
          type: 'page',
          path: '/mailer/emails',
          name: t('Email List'),
          component: EmailListPage,
        },
        {
          type: 'page',
          path: '/mailer/emails/new',
          name: t('Compose New Email'),
          component: EmailComposerPage,
          omitFromNav: true,
        },
        {
          type: 'page',
          path: '/mailer/emails/:id/edit',
          name: t('Edit Email'),
          component: EmailComposerPage,
          omitFromNav: true,
        },
        {
          type: 'page',
          path: '/mailer/emails/:id/recipients',
          name: t('Select Recipients'),
          component: SelectRecipientsPage,
          omitFromNav: true,
        },
        {
          type: 'page',
          path: '/mailer/jobs/:id/preview',
          name: t('Send Preview'),
          component: SendPreviewPage,
          omitFromNav: true,
        },
        {
          type: 'page',
          path: '/mailer/jobs/:id/progress',
          name: t('Send Progress'),
          component: SendProgressPage,
          omitFromNav: true,
        },
        {
          type: 'page',
          path: '/mailer/jobs/:id/summary',
          name: t('Sent Email'),
          component: SentSummaryPage,
          omitFromNav: true,
        },
        {
          type: 'page',
          path: '/mailer/settings',
          name: t('Email Settings'),
          component: MailerSettingsPage,
        },
      ],
    },
    {
      type: 'page',
      path: '/placement',
      name: t('Placement'),
      icon: 'location',
      component: PlacementEditor,
      options: { legacyLayout: true },
      requiresPermissions: { vendorPlacement: 'READ' },
    },
    {
      type: 'page',
      path: '/placement-legacy',
      name: t('Placement (Old)'),
      icon: 'location',
      component: PlacementEditorLegacy,
      options: { legacyLayout: true },
      requiresPermissions: { vendorPlacement: 'READ' },
    },
    {
      type: 'page',
      path: '/maps',
      name: t('Maps'),
      icon: 'explore',
      component: MapEditor,
      options: { legacyLayout: true },
      requiresPermissions: { vendorPlacement: 'READ' },
    },
    {
      type: 'page',
      path: '/embed',
      name: t('Embeds'),
      icon: 'code',
      component: EmbedInfo,
      options: { legacyLayout: true },
    },
    {
      type: 'parent',
      name: t('Vendor Portal'),
      icon: 'libraryBooks',
      requiresPermissions: { marketPages: 'READ' },
      routes: [
        {
          type: 'page',
          path: '/portal/pages',
          name: t('Pages'),
          component: VendorPortalPages,
          requiresPermissions: { marketPages: 'READ' },
        },
        {
          type: 'page',
          path: '/portal/settings',
          name: t('Settings'),
          component: VendorPortalSettings,
          options: { legacyLayout: true },
          requiresPermissions: { marketPages: 'READ' },
        },
      ],
    },
    {
      type: 'page',
      path: '/vendor-change-requests',
      name: t('Vendor Change Requests'),
      icon: 'check',
      component: VendorChangesList,
      options: { legacyLayout: true },
      requiresPermissions: { vendors: 'READ' },
    },
    {
      type: 'page',
      path: '/vendor-change/:id',
      name: t('Vendor Change Details'),
      component: VendorChangeDetails,
      options: { legacyLayout: true },
      omitFromNav: true,
      requiresPermissions: { vendors: 'READ' },
    },
    {
      type: 'parent',
      name: t('Settings'),
      icon: 'settings',
      requiresPermissions: { settings: 'READ' },
      routes: [
        {
          type: 'page',
          path: '/settings/accounting',
          name: t('Accounting & Invoicing'),
          component: InvoiceSettings,
          options: { legacyLayout: true },
          requiresPermissions: { settings: 'READ' },
        },
        {
          type: 'page',
          path: '/settings/approval',
          name: t('Approval Email'),
          component: EmailSettings,
          props: {
            title: 'Approval Email',
            titleVerb: 'approve',
            settingsKey: 'approval_email',
          },
          options: { legacyLayout: true },
          requiresPermissions: { settings: 'READ' },
        },
        {
          type: 'page',
          path: '/settings/waitlisted',
          name: t('Waitlisted Email'),
          component: EmailSettings,
          props: {
            title: 'Waitlisted Email',
            titleVerb: 'waitlist',
            settingsKey: 'waitlisted_email',
          },
          options: { legacyLayout: true },
          requiresPermissions: { settings: 'READ' },
        },
        {
          type: 'page',
          path: '/settings/declined',
          name: t('Declined Email'),
          component: EmailSettings,
          props: {
            title: 'Declined Email',
            titleVerb: 'decline',
            settingsKey: 'declined_email',
          },
          options: { legacyLayout: true },
          requiresPermissions: { settings: 'READ' },
        },
        {
          type: 'page',
          path: '/settings/styling',
          name: t('Embed Styling'),
          component: CustomStyle,
          options: { legacyLayout: true },
          requiresPermissions: { settings: 'READ' },
        },
        {
          type: 'page',
          path: '/settings/seasons',
          name: t('Seasons'),
          component: SeasonsPage,
          requiresPermissions: { settings: 'READ' },
        },
        {
          type: 'page',
          path: '/settings/designation',
          name: t('Stall Names'),
          component: SpotDesignation,
          options: { legacyLayout: true },
          requiresPermissions: { settings: 'READ' },
        },
        {
          type: 'page',
          path: '/settings/integrations',
          name: t('3rd Party Integrations'),
          component: IntegrationSettings,
          options: { legacyLayout: true },
          requiresPermissions: { settings: 'WRITE' },
        },
      ],
    },
    {
      type: 'page',
      path: '/admin/vendor-accounts',
      name: t('Vendor Profile Registrations'),
      icon: 'vendorProfile',
      component: VendorAccountsPage,
      options: { legacyLayout: true },
      requiresPermissions: { vendors: 'READ' },
    },
    {
      type: 'page',
      path: '/users',
      name: t('Users'),
      icon: 'accountBox',
      component: UsersPage,
      options: { legacyLayout: true },
      requiresPermissions: { users: 'READ' },
    },
    {
      type: 'parent',
      name: t('Admin'),
      icon: 'accountBox',
      // TODO: Change this to a special type of user access later
      system_admin: true,
      routes: [
        {
          type: 'page',
          path: '/admin/accounts',
          name: t('Accounts'),
          component: AccountManagementPage,
          system_admin: true,
        },
        {
          type: 'page',
          path: '/admin/accounts/new',
          name: t('Create New Account'),
          component: AccountNewPage,
          system_admin: true,
          omitFromNav: true,
        },
        {
          type: 'page',
          path: '/admin/accounts/:id',
          name: t('Account Details'),
          component: AccountDetailsPage,
          system_admin: true,
          omitFromNav: true,
        },
      ],
    },
  ] as const);
}

function defineRoutes<T extends ReadonlyArray<RouteDescriptor>>(routes: T): T {
  return routes;
}

export type RoutePath = ExtractRoutePath<ReturnType<typeof getRoutes>>;

export const useNavigation = createUseNavigation<RoutePath>();

export const Link = createLink<RoutePath>();
export const Button = createButton<RoutePath>();
export const Redirect = createRedirect<RoutePath>();
